import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

const PRODUCT_TYPES = ["prepared", "on_demand"] as const;

/**
 * Skills "bulk" que permiten al LLM ejecutar muchas acciones del mismo tipo
 * en UNA SOLA llamada al upstream. Reduce drásticamente los roundtrips
 * cuando hay que crear/eliminar muchos elementos similares.
 */

export const bulkCreateProductsSkill: Skill = {
  id: "bulkCreateProducts",
  description:
    "Crea VARIOS productos de una sola vez. Usa esto en vez de llamar createProduct N veces — reduce drásticamente el costo y evita errores de modelo. Cada item: { name, price, type?, description?, categoryId? }.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      products: {
        type: "array",
        description:
          "Array de productos a crear. Cada uno con name, price y opcionalmente type/description/categoryId.",
        items: {
          type: "object",
          properties: {
            name: { type: "string" },
            price: { type: "number" },
            type: { type: "string", enum: [...PRODUCT_TYPES] },
            description: { type: "string" },
            categoryId: { type: "string" },
          },
          required: ["name", "price"],
        },
      },
    },
    required: ["restaurantId", "products"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const items = Array.isArray(args.products) ? args.products : [];
    if (!restaurantId || items.length === 0) {
      return { ok: false, error: "Faltan restaurantId o products[]." };
    }

    // Normalizar y validar cada producto
    const normalized = items
      .map((p: any) => {
        const name = String(p?.name ?? "").trim();
        const price = Number(p?.price);
        const type = (p?.type as string) || "prepared";
        if (!name || !Number.isFinite(price) || price < 0) return null;
        if (!PRODUCT_TYPES.includes(type as (typeof PRODUCT_TYPES)[number])) {
          return null;
        }
        return {
          name,
          price,
          type,
          description: p?.description ? String(p.description) : undefined,
          categoryId: p?.categoryId ? String(p.categoryId) : undefined,
        };
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);

    if (normalized.length === 0) {
      return { ok: false, error: "Ningún producto válido." };
    }

    const ok = await ctx.confirmation.confirm({
      title: `Crear ${normalized.length} productos`,
      description: "Vas a crear varios productos en bloque.",
      details: normalized.slice(0, 8).map((p) => ({
        label: p.name,
        value: `$${p.price.toLocaleString()}`,
      })),
      confirmLabel: `Crear los ${normalized.length}`,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Creando ${normalized.length} productos…`,
      successMsg: `✓ ${normalized.length} productos creados`,
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        const created: Array<{ id?: string; name: string }> = [];
        const failed: Array<{ name: string; error: string }> = [];

        // Pre-check duplicados con UNA sola consulta
        let existingProducts: any[] = [];
        try {
          const { data: existing } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/products?limit=500`,
            ctx.accessToken,
          );
          existingProducts = existing?.items ?? existing?.data ?? [];
        } catch {
          // ignore — intentamos crear igual
        }
        const existingNames = new Set(
          existingProducts.map((p) =>
            String(p.name).trim().toLowerCase(),
          ),
        );

        for (const p of normalized) {
          if (existingNames.has(p.name.toLowerCase())) {
            failed.push({ name: p.name, error: "ya existe" });
            continue;
          }
          try {
            const body: Record<string, unknown> = {
              name: p.name,
              price: p.price,
              type: p.type,
            };
            if (p.description) body.description = p.description;
            if (p.categoryId) body.categoryId = p.categoryId;
            const { data } = await apiWithAuth.post(
              `/restaurants/${restaurantId}/products`,
              body,
              ctx.accessToken,
            );
            created.push({ id: data?.id, name: p.name });
            existingNames.add(p.name.toLowerCase());
          } catch (err: any) {
            failed.push({
              name: p.name,
              error: err?.response?.data?.message ?? "error",
            });
          }
        }

        emitCopilotMutation({
          scope: "products",
          action: "created",
          restaurantId,
        });

        return {
          ok: true as const,
          result: {
            createdCount: created.length,
            failedCount: failed.length,
            created: created.slice(0, 30),
            failed: failed.slice(0, 10),
          },
          humanSummary:
            failed.length === 0
              ? `${created.length} producto(s) creados.`
              : `${created.length} creados, ${failed.length} fallidos.`,
        };
      },
    });
  },
};

export const bulkDeleteCategoriesSkill: Skill = {
  id: "bulkDeleteCategories",
  description:
    "Elimina VARIAS categorías a la vez. Para cada una: si tiene productos, los mueve a 'sin categoría' automáticamente; luego elimina la categoría. Ideal para limpiar duplicados.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      categoryIds: {
        type: "array",
        items: { type: "string" },
        description: "IDs de categorías a eliminar.",
      },
    },
    required: ["restaurantId", "categoryIds"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const ids = Array.isArray(args.categoryIds)
      ? args.categoryIds.map(String).filter(Boolean)
      : [];
    if (!restaurantId || ids.length === 0) {
      return { ok: false, error: "Faltan restaurantId o categoryIds[]." };
    }

    const ok = await ctx.confirmation.confirm({
      title: `Eliminar ${ids.length} categoría(s)`,
      description:
        "Cada categoría con productos: los productos se moverán a 'Sin categoría'.",
      confirmLabel: `Eliminar las ${ids.length}`,
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Eliminando ${ids.length} categorías…`,
      successMsg: `✓ ${ids.length} eliminadas`,
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        const deleted: string[] = [];
        const failed: Array<{ id: string; error: string }> = [];
        let totalProductsMoved = 0;

        for (const id of ids) {
          try {
            const { data } = await apiWithAuth.delete(
              `/restaurants/${restaurantId}/product-categories/${id}`,
              ctx.accessToken,
            );
            deleted.push(id);
            totalProductsMoved += data?.productsMoved ?? 0;
          } catch (err: any) {
            failed.push({
              id,
              error: err?.response?.data?.message ?? "error",
            });
          }
        }

        emitCopilotMutation({
          scope: "categories",
          action: "deleted",
          restaurantId,
        });
        if (totalProductsMoved > 0) {
          emitCopilotMutation({
            scope: "products",
            action: "updated",
            restaurantId,
          });
        }

        return {
          ok: true as const,
          result: {
            deletedCount: deleted.length,
            failedCount: failed.length,
            productsMoved: totalProductsMoved,
            failed: failed.slice(0, 10),
          },
          humanSummary:
            totalProductsMoved > 0
              ? `${deleted.length} eliminadas, ${totalProductsMoved} producto(s) movidos a "Sin categoría".`
              : `${deleted.length} categoría(s) eliminadas.`,
        };
      },
    });
  },
};

export const bulkCreateCategoriesSkill: Skill = {
  id: "bulkCreateCategories",
  description:
    "Crea VARIAS categorías de una sola vez. Ideal para inicializar un menú: 'Entradas', 'Principales', 'Postres', 'Bebidas' en una sola llamada. Auto-skipea las que ya existen.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      names: {
        type: "array",
        items: { type: "string" },
        description: "Nombres de las categorías a crear.",
      },
    },
    required: ["restaurantId", "names"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const names = Array.isArray(args.names)
      ? args.names
          .map((n: any) => String(n).trim())
          .filter((n: string) => n.length > 0)
      : [];
    if (!restaurantId || names.length === 0) {
      return { ok: false, error: "Faltan restaurantId o names[]." };
    }

    const ok = await ctx.confirmation.confirm({
      title: `Crear ${names.length} categoría(s)`,
      details: names.slice(0, 10).map((n) => ({ label: "•", value: n })),
      confirmLabel: `Crear las ${names.length}`,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Creando ${names.length} categorías…`,
      successMsg: `✓ ${names.length} creadas`,
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        const created: Array<{ id?: string; name: string }> = [];
        const skipped: string[] = [];
        const failed: Array<{ name: string; error: string }> = [];

        // Pre-check duplicados con UNA sola consulta
        let existingNames = new Set<string>();
        try {
          const { data: existing } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/product-categories`,
            ctx.accessToken,
          );
          const items = existing?.items ?? existing?.data ?? [];
          existingNames = new Set(
            items.map((c: any) => String(c.name).trim().toLowerCase()),
          );
        } catch {
          // ignore
        }

        for (const name of names) {
          if (existingNames.has(name.toLowerCase())) {
            skipped.push(name);
            continue;
          }
          try {
            const { data } = await apiWithAuth.post(
              `/restaurants/${restaurantId}/product-categories`,
              { name },
              ctx.accessToken,
            );
            created.push({ id: data?.id, name });
            existingNames.add(name.toLowerCase());
          } catch (err: any) {
            failed.push({
              name,
              error: err?.response?.data?.message ?? "error",
            });
          }
        }

        emitCopilotMutation({
          scope: "categories",
          action: "created",
          restaurantId,
        });

        return {
          ok: true as const,
          result: {
            createdCount: created.length,
            skippedCount: skipped.length,
            failedCount: failed.length,
            created,
            skipped,
            failed,
          },
          humanSummary: `${created.length} creada(s)${skipped.length ? `, ${skipped.length} ya existían` : ""}${failed.length ? `, ${failed.length} fallidas` : ""}.`,
        };
      },
    });
  },
};
